import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { CafeShell } from "./CafeShell";

const STORAGE_KEY = "soma_admin_key";

export default function AdminFeedbackPage() {
  const [authed, setAuthed] = useState(null); // null=checking, false=needs key, true=ok
  const [keyInput, setKeyInput] = useState("");
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // try to use saved key
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) { setAuthed(false); return; }
    setKeyInput(saved);
    loadWithKey(saved);
  }, []);

  const loadWithKey = async (k) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { key: k });
      const res = await axios.get(`${API}/admin/feedback`, { headers: { "X-Admin-Key": k } });
      localStorage.setItem(STORAGE_KEY, k);
      setData(res.data);
      setAuthed(true);
      setError(null);
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
      setError(err.response?.status === 401 ? "That key doesn't match." : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    loadWithKey(keyInput.trim());
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setData(null);
    setKeyInput("");
  };

  if (authed === null || loading && !data) {
    return (
      <CafeShell testId="admin-loading">
        <p style={{ fontFamily: "'Space Mono', monospace", textAlign: "center", color: "var(--terracotta-soft)", fontSize: 12, letterSpacing: "0.2em" }}>// loading…</p>
      </CafeShell>
    );
  }

  if (!authed) {
    return (
      <CafeShell testId="admin-login">
        <p className="section-marker" style={{ textAlign: "center" }}>admin · for soma only</p>
        <form onSubmit={handleSubmit} style={{ maxWidth: 360, margin: "30px auto 0" }}>
          <input
            type="password"
            data-testid="admin-key-input"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="enter admin key"
            style={{
              width: "100%",
              border: "1px solid rgba(143, 196, 224, 0.32)",
              borderRadius: 0,
              padding: "14px 18px",
              fontFamily: "'Space Mono', monospace",
              fontSize: 14,
              color: "var(--cream)",
              background: "rgba(255, 255, 255, 0.025)",
              outline: "none",
              textAlign: "center",
              letterSpacing: "0.08em",
            }}
            autoFocus
          />
          {error && <p className="error-banner" style={{ marginTop: 12 }}>{error}</p>}
          <button data-testid="admin-submit" className="primary-btn" type="submit" style={{ marginTop: 16 }}>
            unlock
          </button>
        </form>
      </CafeShell>
    );
  }

  // authed view
  const { reflections = [], reminders = [], reflection_count, reminder_count, cafe_count } = data || {};
  return (
    <CafeShell testId="admin-feedback-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <h2 className="italic-quote" style={{ fontSize: 28, color: "var(--cream)", margin: 0 }}>Feedback</h2>
        <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
          <a href="/admin/cafes" data-testid="nav-admin-cafes" style={{ color: "var(--terracotta-soft)", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", borderBottom: "1px dotted var(--terracotta-soft)", paddingBottom: 1 }}>
            ← cafes
          </a>
          <button onClick={logout} data-testid="admin-logout" style={{ background: "transparent", border: "none", color: "var(--terracotta-soft)", cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            sign out
          </button>
        </div>
      </div>

      {/* counts row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 32 }}>
        <CountTile label="reflections" value={reflection_count} />
        <CountTile label="email opt-ins" value={reminder_count} />
        <CountTile label="cafes signed up" value={cafe_count} />
      </div>

      <p className="section-marker">recent sessions</p>
      {reflections.length === 0 && (
        <p style={{ textAlign: "center", color: "var(--dim)", fontStyle: "italic", marginTop: 24 }}>
          No reflections submitted yet. Once friends start filling out the journey, they'll show here.
        </p>
      )}
      <div style={{ display: "grid", gap: 16, marginTop: 12 }}>
        {reflections.map((r) => (
          <ReflectionCard key={r.id} r={r} />
        ))}
      </div>

      {reminders.length > 0 && (
        <>
          <p className="section-marker" style={{ marginTop: 40 }}>email reminder opt-ins</p>
          <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 13, color: "var(--dim)", margin: "0 0 16px" }}>
            These people asked for "one gentle reminder tomorrow morning." Wire Resend/SendGrid when you're ready and process them.
          </p>
          <div style={{ display: "grid", gap: 8 }}>
            {reminders.map((rm) => (
              <div key={rm.id} style={{ padding: "10px 14px", border: "1px solid rgba(143, 196, 224, 0.2)", display: "flex", justifyContent: "space-between", fontFamily: "'Space Mono', monospace", fontSize: 12 }}>
                <span style={{ color: "var(--cream)" }}>{rm.email}</span>
                <span style={{ color: "var(--dim)" }}>{rm.cafe_slug || "—"} · {rm.status} · {formatTime(rm.created_at)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </CafeShell>
  );
}

function CountTile({ label, value }) {
  return (
    <div style={{
      padding: 16,
      background: "rgba(10, 6, 4, 0.7)",
      border: "1px solid rgba(143, 196, 224, 0.3)",
      textAlign: "center",
    }}>
      <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.2em", color: "var(--terracotta-soft)", margin: "0 0 6px", textTransform: "uppercase" }}>
        // {label}
      </p>
      <p style={{ fontFamily: "'VT323', monospace", fontSize: 34, color: "var(--cream)", margin: 0, lineHeight: 1 }}>
        {value ?? 0}
      </p>
    </div>
  );
}

function ReflectionCard({ r }) {
  const [expanded, setExpanded] = useState(false);
  const hasBefore = Array.isArray(r.wordsBefore) && r.wordsBefore.some(w => (w || "").trim());
  const hasAfter = Array.isArray(r.wordsAfter) && r.wordsAfter.some(w => (w || "").trim());

  return (
    <div className="terminal-corners" style={{
      position: "relative",
      padding: "20px 18px",
      background: "rgba(10, 6, 4, 0.55)",
      border: "1px solid rgba(143, 196, 224, 0.28)",
    }}>
      <span className="tc tc-tl">┌</span><span className="tc tc-tr">┐</span>
      <span className="tc tc-bl">└</span><span className="tc tc-br">┘</span>

      {/* header — when + lens + cafe */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em", color: "var(--terracotta-soft)", textTransform: "uppercase" }}>
        <span>{formatTime(r.timestamp)}</span>
        <span style={{ color: "var(--dim)" }}>
          {(r.cafe_slug || "no-cafe")} · {r.lens || "—"}
        </span>
      </div>

      {/* cup name */}
      {r.cupName && (
        <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 18, color: "var(--cream)", margin: "0 0 14px", textAlign: "center" }}>
          "{r.cupName}"
        </p>
      )}

      {/* whats on mind */}
      {r.whatsOnMind && (
        <Block label="what was on their mind" body={r.whatsOnMind} />
      )}

      {/* macro noticing */}
      {r.macroNoticing && (
        <Block label="what they noticed when their eyes shifted" body={r.macroNoticing} />
      )}

      {/* words before / after */}
      {(hasBefore || hasAfter) && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", color: "var(--terracotta-soft)", margin: "0 0 6px", textTransform: "uppercase" }}>
            // three words shift
          </p>
          <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 15, color: "var(--cream)", margin: 0 }}>
            {hasBefore ? r.wordsBefore.filter(w => (w || "").trim()).join(" · ") : "—"}
            <span style={{ color: "var(--terracotta-soft)", margin: "0 10px" }}>→</span>
            {hasAfter ? r.wordsAfter.filter(w => (w || "").trim()).join(" · ") : "—"}
          </p>
        </div>
      )}

      {/* reflection text */}
      {r.reflectionText && (
        <Block label={`their reflection (${r.reflectionType})`} body={r.reflectionText} />
      )}
      {r.hasVoiceMemo && !r.reflectionText && (
        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--terracotta-soft)", margin: "8px 0", textTransform: "uppercase" }}>
          // voice memo recorded (audio not uploaded — local only in beta)
        </p>
      )}

      {/* share consent / email */}
      {r.shareConsent && (
        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--terracotta-soft)", margin: "8px 0 0" }}>
          // ✓ ok to share publicly{r.notifyEmail ? `  ·  ${r.notifyEmail}` : ""}
        </p>
      )}

      {/* expand reading */}
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          marginTop: 12,
          background: "transparent",
          border: "none",
          color: "var(--terracotta-soft)",
          fontFamily: "'Space Mono', monospace",
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {expanded ? "// hide reading" : "// view full reading"}
      </button>
      {expanded && r.reading && (
        <p style={{ marginTop: 8, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 15, color: "var(--cream-soft)", lineHeight: 1.55, borderLeft: "2px solid var(--terracotta-soft)", paddingLeft: 12 }}>
          {r.reading}
        </p>
      )}
    </div>
  );
}

function Block({ label, body }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", color: "var(--terracotta-soft)", margin: "0 0 4px", textTransform: "uppercase" }}>
        // {label}
      </p>
      <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 15, color: "var(--cream)", margin: 0, lineHeight: 1.45 }}>
        {body}
      </p>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}
